import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { List, useNotify } from 'react-admin';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import { PAGE_GUTTER } from '../../core/theme/layout';
import {
  MonthlyFeeDefault,
  MonthlyFeeDefaultService,
} from '../../services/billing/monthlyFeeDefaultService';

const monthlyFeeDefaultService = new MonthlyFeeDefaultService();

/**
 * Domyslny miesieczny abonament na kazda walute (PLN, EUR, USD) - dla restauracji bez
 * wlasnej kwoty. Kwoty sa niezalezne, nie liczone po kursie.
 */
export default function MonthlyFeeDefaultsPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader back={<BackButton />} title={t('monthlyFeeDefaults.title')} />
      <List
        resource="monthlyFeeDefaults"
        title={t('resources.monthlyFeeDefaults')}
        actions={false}
        queryOptions={{ enabled: false }}
        pagination={false}
        sx={{
          '& .RaList-main': { px: PAGE_GUTTER },
          // Jak WoltCheckerPage: <List> tylko jako rama strony, bez pustej karty listy.
          '& .RaList-content': {
            border: 'none !important',
            borderRadius: '0 !important',
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        }}
      >
        <MonthlyFeeDefaultsForm />
      </List>
    </>
  );
}

const MonthlyFeeDefaultsForm: React.FC = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const [fees, setFees] = React.useState<MonthlyFeeDefault[] | null>(null);
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [savingCurrency, setSavingCurrency] = React.useState<string | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);

  React.useEffect(() => {
    monthlyFeeDefaultService
      .list()
      .then((list) => {
        setFees(list);
        setDrafts(
          Object.fromEntries(list.map((fee) => [fee.currency, fee.monthlyFee?.toString() ?? ''])),
        );
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoadFailed(true);
      });
  }, []);

  const isValid = (value: string) =>
    value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

  const save = async (currency: string) => {
    const value = drafts[currency] ?? '';
    if (!isValid(value)) return;
    setSavingCurrency(currency);
    try {
      const saved = await monthlyFeeDefaultService.update(currency, Number(value));
      setFees(
        (current) => current?.map((fee) => (fee.currency === currency ? saved : fee)) ?? null,
      );
      notify(t('monthlyFeeDefaults.saved', { currency }), { type: 'success' });
    } catch (error: any) {
      Sentry.captureException(error);
      notify(error?.response?.data?.error ?? t('monthlyFeeDefaults.saveFailed'), { type: 'error' });
    } finally {
      setSavingCurrency(null);
    }
  };

  if (loadFailed) return <Alert severity="error">{t('monthlyFeeDefaults.loadFailed')}</Alert>;
  if (!fees) return <CircularProgress size={24} />;

  return (
    <Box sx={{ maxWidth: 600, pb: 'var(--space-8)' }}>
      <Paper
        elevation={0}
        sx={{
          p: 'var(--space-5)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <Typography
          sx={{
            mb: 'var(--space-4)',
            color: 'var(--color-text-secondary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {t('monthlyFeeDefaults.description')}
        </Typography>
        <Stack spacing={2}>
          {fees.map((fee) => {
            const value = drafts[fee.currency] ?? '';
            const unchanged = value === (fee.monthlyFee?.toString() ?? '');
            const invalid = value !== '' && !isValid(value);
            return (
              <Stack key={fee.currency} direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  label={`${t('monthlyFeeDefaults.monthlyFee')} (${fee.currency})`}
                  placeholder={t('monthlyFeeDefaults.notSet')}
                  value={value}
                  onChange={(event) =>
                    setDrafts((current) => ({ ...current, [fee.currency]: event.target.value }))
                  }
                  error={invalid}
                  helperText={invalid ? t('monthlyFeeDefaults.invalid') : ' '}
                  slotProps={{
                    htmlInput: { min: 0, step: '0.01' },
                    input: {
                      endAdornment: <InputAdornment position="end">{fee.currency}</InputAdornment>,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => save(fee.currency)}
                  disabled={unchanged || !isValid(value) || savingCurrency !== null}
                  sx={{ minWidth: 110, height: 40 }}
                >
                  {savingCurrency === fee.currency ? (
                    <CircularProgress size={18} />
                  ) : (
                    t('monthlyFeeDefaults.save')
                  )}
                </Button>
              </Stack>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
};
